import axios, { type AxiosResponse, AxiosError } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

/**
 * File types supported by the upload system
 */
export const FileType = {
  VIDEO: "VIDEO",
  PDF: "PDF",
  IMAGE: "IMAGE",
  DOCUMENT: "DOCUMENT",
} as const;

export type FileTypeValue = (typeof FileType)[keyof typeof FileType];

/**
 * File categories for organization
 */
export const FileCategory = {
  CERTIFICATE: "CERTIFICATE",
  LEARNING_MATERIAL: "LEARNING_MATERIAL",
  GENERAL: "GENERAL",
} as const;

export type FileCategoryValue =
  (typeof FileCategory)[keyof typeof FileCategory];

/**
 * Base interface for upload request data
 */
export interface UploadRequestDto {
  filename: string;
  contentType: string;
  fileSize: number;
  fileType: FileTypeValue;
  category: FileCategoryValue;
  materialId?: string;
  title?: string;
}

/**
 * Extended interface for learning material uploads with additional metadata
 */
export interface LearningMaterialUploadRequestDto extends UploadRequestDto {
  fileType: typeof FileType.VIDEO | typeof FileType.PDF;
  learningMaterial: {
    topicId: string;
    title: string;
    description?: string;
    displayOrder?: number;
    tags?: string[];
    allowDownload?: boolean;
    drmEnabled?: boolean;
  };
}

/**
 * Response from upload URL request containing presigned URL and session info
 */
export interface UploadUrlResponse {
  success: boolean;
  data: {
    sessionId: string;
    uploadUrl: string;
    uploadKey: string;
    expiresAt: string;
  };
}

/**
 * Response from LMS upload URL request with additional learning material info
 */
export interface LMSUploadUrlResponse {
  success: boolean;
  message: string;
  data: {
    sessionId: string;
    uploadUrl: string;
    uploadKey: string;
    fileType: FileTypeValue;
    expiresAt: string;
    learningMaterial: {
      title: string;
      topicId: string;
      status: string;
    };
  };
}

/**
 * Request payload for completing an upload
 */
export interface CompleteUploadRequest {
  sessionId: string;
  actualFileSize?: number;
}

/**
 * Response from completing a regular upload
 */
export interface CompleteUploadResponse {
  success: boolean;
  message: string;
  data: {
    fileUploadId: string;
    filename: string;
    fileType: FileTypeValue;
    category: FileCategoryValue;
    fileSize: number;
    uploadedAt: string;
  };
}

/**
 * Response from completing an LMS upload
 */
export interface CompleteLMSUploadResponse {
  success: boolean;
  message: string;
  data: {
    materialId: string;
    fileUploadId: string;
    title: string;
    status: "PROCESSING" | "READY";
    processingJobId?: string;
    isActive?: boolean;
  };
}

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T = Record<string, unknown>> {
  success: boolean;
  message?: string;
  data?: T;
}

/**
 * API error interface
 */
export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

// ============================================================================
// AXIOS CONFIGURATION
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

const uploadApiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/upload`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for error handling
// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(uploadApiClient);

uploadApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const errorData = error.response?.data as
      | { message?: string; error?: string }
      | undefined;

    const apiError: ApiError = {
      message:
        errorData?.message || error.message || "An upload error occurred",
      statusCode: error.response?.status || 0,
      error: errorData?.error,
    };
    return Promise.reject(apiError);
  }
);

// ============================================================================
// UPLOAD API FUNCTIONS
// ============================================================================

/**
 * Request a presigned URL for regular file upload
 * @param uploadRequest - Upload request configuration
 * @returns Promise resolving to upload URL and session information
 * @throws ApiError if request fails or validation errors occur
 */
export async function requestUploadUrl(
  uploadRequest: UploadRequestDto
): Promise<UploadUrlResponse> {
  const response = await uploadApiClient.post<UploadUrlResponse>(
    "/request",
    uploadRequest
  );
  return response.data;
}

/**
 * Complete a regular file upload after uploading to S3
 * @param completeRequest - Completion request with session ID
 * @returns Promise resolving to upload completion confirmation
 * @throws ApiError if session is invalid or file not found
 */
export async function completeUpload(
  completeRequest: CompleteUploadRequest
): Promise<CompleteUploadResponse> {
  const response = await uploadApiClient.post<CompleteUploadResponse>(
    "/complete",
    completeRequest
  );
  return response.data;
}

/**
 * Request a presigned URL for LMS learning material upload
 * @param uploadRequest - Learning material upload request configuration
 * @returns Promise resolving to upload URL and learning material session information
 * @throws ApiError if topic not found or validation errors occur
 */
export async function requestLMSUploadUrl(
  uploadRequest: LearningMaterialUploadRequestDto
): Promise<LMSUploadUrlResponse> {
  const response = await uploadApiClient.post<LMSUploadUrlResponse>(
    "/lms/request",
    uploadRequest
  );
  return response.data;
}

/**
 * Complete an LMS learning material upload after uploading to S3
 * @param completeRequest - Completion request with session ID
 * @returns Promise resolving to learning material creation confirmation
 * @throws ApiError if session is invalid, file not found, or processing fails
 */
export async function completeLMSUpload(
  completeRequest: CompleteUploadRequest
): Promise<CompleteLMSUploadResponse> {
  const response = await uploadApiClient.post<CompleteLMSUploadResponse>(
    "/lms/complete",
    completeRequest
  );
  return response.data;
}

// ============================================================================
// S3 UPLOAD UTILITY FUNCTIONS
// ============================================================================

/**
 * Upload file directly to S3 using presigned URL
 * @param file - File object to upload
 * @param uploadUrl - Presigned URL from upload request
 * @param contentType - MIME type of the file
 * @param onProgress - Optional progress callback function
 * @returns Promise resolving when upload completes
 * @throws Error if upload fails
 */
export async function uploadToS3(
  file: File,
  uploadUrl: string,
  contentType: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("S3 upload failed due to network error"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("S3 upload was aborted"));
    });

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(file);
  });
}

// ============================================================================
// HIGH-LEVEL WORKFLOW FUNCTIONS
// ============================================================================

/**
 * Complete file upload workflow: request URL, upload to S3, and complete
 * @param file - File object to upload
 * @param uploadRequest - Upload request configuration (without file details)
 * @param onProgress - Optional progress callback for S3 upload
 * @returns Promise resolving to upload completion response
 * @throws ApiError if any step in the workflow fails
 */
export async function uploadFile(
  file: File,
  uploadRequest: Omit<
    UploadRequestDto,
    "filename" | "contentType" | "fileSize"
  >,
  onProgress?: (progress: number) => void
): Promise<CompleteUploadResponse> {
  try {
    // Prepare complete upload request
    const completeRequest: UploadRequestDto = {
      ...uploadRequest,
      filename: file.name,
      contentType: file.type,
      fileSize: file.size,
    };

    // Step 1: Request upload URL
    const urlResponse = await requestUploadUrl(completeRequest);

    // Step 2: Upload to S3
    await uploadToS3(file, urlResponse.data.uploadUrl, file.type, onProgress);

    // Step 3: Complete upload
    return await completeUpload({
      sessionId: urlResponse.data.sessionId,
      actualFileSize: file.size,
    });
  } catch (error) {
    // Re-throw ApiError or wrap other errors
    if (error && typeof error === "object" && "statusCode" in error) {
      throw error as ApiError;
    }

    const apiError: ApiError = {
      message: `Upload workflow failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      statusCode: 0,
    };
    throw apiError;
  }
}

/**
 * Complete LMS learning material upload workflow: request URL, upload to S3, and complete
 * @param file - File object to upload (must be video or PDF)
 * @param uploadRequest - Learning material upload request configuration (without file details)
 * @param onProgress - Optional progress callback for S3 upload
 * @returns Promise resolving to learning material creation response
 * @throws ApiError if any step in the workflow fails
 */
export async function uploadLearningMaterial(
  file: File,
  uploadRequest: Omit<
    LearningMaterialUploadRequestDto,
    "filename" | "contentType" | "fileSize" | "category"
  >,
  onProgress?: (progress: number) => void
): Promise<CompleteLMSUploadResponse> {
  try {
    // Prepare complete upload request
    const completeRequest: LearningMaterialUploadRequestDto = {
      filename: file.name,
      contentType: file.type,
      fileSize: file.size,
      category: FileCategory.LEARNING_MATERIAL,
      ...uploadRequest,
    };

    // Step 1: Request upload URL
    const urlResponse = await requestLMSUploadUrl(completeRequest);

    // Step 2: Upload to S3
    await uploadToS3(file, urlResponse.data.uploadUrl, file.type, onProgress);

    // Step 3: Complete upload and create learning material
    return await completeLMSUpload({
      sessionId: urlResponse.data.sessionId,
      actualFileSize: file.size,
    });
  } catch (error) {
    // Re-throw ApiError or wrap other errors
    if (error && typeof error === "object" && "statusCode" in error) {
      throw error as ApiError;
    }

    const apiError: ApiError = {
      message: `LMS upload workflow failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      statusCode: 0,
    };
    throw apiError;
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Utility function to validate file before upload
 * @param file - File to validate
 * @param fileType - Expected file type
 * @returns Validation result with success status and error message
 */
export function validateFile(
  file: File,
  fileType: FileTypeValue
): { valid: boolean; error?: string } {
  const validations = {
    [FileType.VIDEO]: {
      contentTypes: [
        "video/mp4",
        "video/webm",
        "video/quicktime",
        "video/x-msvideo",
        "video/avi",
      ],
      maxSize: 2 * 1024 * 1024 * 1024, // 2GB
    },
    [FileType.PDF]: {
      contentTypes: ["application/pdf"],
      maxSize: 100 * 1024 * 1024, // 100MB
    },
    [FileType.IMAGE]: {
      contentTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
      maxSize: 10 * 1024 * 1024, // 10MB
    },
    [FileType.DOCUMENT]: {
      contentTypes: [
        "application/pdf",
        "application/msword",
        "text/plain",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ],
      maxSize: 50 * 1024 * 1024, // 50MB
    },
  };

  const validation = validations[fileType];
  if (!validation) {
    return { valid: false, error: `Unsupported file type: ${fileType}` };
  }

  if (!validation.contentTypes.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      error: `Invalid content type for ${fileType}. Allowed: ${validation.contentTypes.join(", ")}`,
    };
  }

  if (file.size > validation.maxSize) {
    const maxSizeMB = Math.round(validation.maxSize / (1024 * 1024));
    return {
      valid: false,
      error: `File size cannot exceed ${maxSizeMB}MB for ${fileType}`,
    };
  }

  return { valid: true };
}

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Get file extension from filename
 * @param filename - Name of the file
 * @returns File extension (without dot)
 */
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
}

/**
 * Check if a file type supports the given file
 * @param file - File to check
 * @param fileType - Expected file type
 * @returns True if file type is supported
 */
export function isFileTypeSupported(
  file: File,
  fileType: FileTypeValue
): boolean {
  const validation = validateFile(file, fileType);
  return validation.valid;
}
